import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { Icon } from './Icon';

/* ==========================================================================
   نمط الملف — ترتيب الأقسام وإخفاؤها وتكبير واحدها
   ========================================================================== */

/**
 * ملفّ العميل شاشةٌ يقرؤها ثلاثة أناسٍ مختلفين.
 *
 * المندوب يريد سجلّ التعامل أولًا، والمحاسب يريد الفواتير وحدّ الائتمان،
 * ومدير الحساب يريد جهات الاتصال و«ما جرى». وترتيبٌ واحدٌ يخدم واحدًا
 * منهم ويُتعب اثنين — فصار الترتيب تفضيلًا لا قدَرًا:
 *
 *   - «تغيير النمط» يُدخل الصفحة وضعَ التحرير: كل قسمٍ كبير يُسحب إلى
 *     موضعٍ آخر — في عموده أو إلى العمود المقابل — ويُخفى بعلامة ×.
 *   - المخفيّ لا يُنسى: شريطٌ أعلى الصفحة يعرضه ويعيده بنقرة.
 *   - وزرّ التكبير يفتح القسم وحده على الشاشة كاملةً — كنافذة ماك —
 *     ويُغلق بزرّه أو بمفتاح Esc.
 *
 * والتفضيل يُحفظ في المتصفّح بمفتاحٍ لكل نوع ملف، فيبقى بين الجلسات.
 *
 * **كيف ينتقل القسم بين العمودين بلا نقل شفرته؟**
 *
 * كل قسمٍ يبقى مكتوبًا في الصفحة حيث يقرؤه المطوّر طبيعيًّا — سجلّ
 * التعامل في العمود الرئيس، وجهات الاتصال في الجانبي. فإن اختار
 * المستخدم غير ذلك، رُسم القسم في العمود الآخر عبر بوّابة React
 * (`createPortal`) لا بنقل شفرته، وترتيبه داخل عموده بخاصية `order`.
 * فشجرة React تبقى كما كُتبت — بحالتها وسياقها — والشاشة تُطيع
 * المستخدم.
 */

/**
 * أعمدةُ النمط ثلاثة.
 *
 * «رئيسٌ» و«جانبيّ» يكفيان ملفَّ سجلٍّ: متنٌ وهامش. وشاشةُ التقارير فيها
 * ما لا يُقسَم — قمعٌ برسمه، وخطُّ شهورٍ باثني عشر عمودًا، وجدولٌ بأعمدةٍ
 * كثيرة — يُخنق في نصف العرض ويُقرأ في كلِّه. فعمودٌ ثالثٌ يمتدّ عرضَ
 * الشاشة، ويُنقل إليه ومنه بالسحب كأخويه.
 */
export type ColumnKey = 'full' | 'main' | 'side';

/** ترتيبُ الأعمدة في الصفحة — وبه تُمسح في النقل والبحث. */
const COLUMNS: ColumnKey[] = ['full', 'main', 'side'];

export interface SectionDef {
  key: string;
  title: string;
  column: ColumnKey;
}

interface Layout {
  order: Record<ColumnKey, string[]>;
  hidden: string[];
}

interface LayoutState {
  editing: boolean;
  zoomed: string | null;
  hidden: Set<string>;
  dragging: string | null;
  setDragging: (key: string | null) => void;
  move: (dragKey: string, overKey: string) => void;
  moveToColumn: (dragKey: string, column: ColumnKey) => void;
  hide: (key: string) => void;
  setZoomed: (key: string | null) => void;
  titleOf: (key: string) => string;
  /** أين يُرسم كل قسم وبأيّ ترتيب. */
  placement: (key: string) => { column: ColumnKey; index: number } | null;
  columnEl: (column: ColumnKey) => HTMLElement | null;
  registerColumn: (column: ColumnKey, el: HTMLElement | null) => void;
  zoomEl: HTMLElement | null;
}

const Ctx = createContext<LayoutState | null>(null);
/** العمود الذي كُتب فيه القسم — منه يُعرف أيحتاج بوّابةً أم لا. */
const HostColumn = createContext<ColumnKey>('main');

function readSaved(storageKey: string): Layout | null {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Layout;
    if (!Array.isArray(parsed?.order?.main) || !Array.isArray(parsed?.order?.side)) return null;
    return { order: parsed.order, hidden: parsed.hidden ?? [] };
  } catch {
    return null;
  }
}

/**
 * حالة النمط.
 *
 * تُستدعى في الصفحة، ويُمرَّر ناتجُها إلى الزرّ والشريط والأعمدة — فزرّ
 * «تغيير النمط» يسكن في رأس الصفحة مع إخوته، والأقسام في متنها.
 */
export function useProfileLayout(storageKey: string, defs: SectionDef[]) {
  const defaults = useMemo<Layout>(
    () => ({
      order: {
        full: defs.filter((d) => d.column === 'full').map((d) => d.key),
        main: defs.filter((d) => d.column === 'main').map((d) => d.key),
        side: defs.filter((d) => d.column === 'side').map((d) => d.key),
      },
      hidden: [],
    }),
    [defs],
  );

  const [layout, setLayout] = useState<Layout>(() => readSaved(storageKey) ?? defaults);
  const [editing, setEditing] = useState(false);
  const [zoomed, setZoomed] = useState<string | null>(null);
  const [dragging, setDragging] = useState<string | null>(null);
  const [cols, setCols] = useState<Record<ColumnKey, HTMLElement | null>>({
    full: null,
    main: null,
    side: null,
  });
  const [zoomEl, setZoomEl] = useState<HTMLElement | null>(null);

  /*
   * المصالحة مع التعريفات الحالية.
   *
   * ترتيبٌ محفوظٌ من نسخةٍ سابقة قد يذكر قسمًا حُذف أو يجهل قسمًا أُضيف.
   * فالمعروف يبقى بترتيبه، والمجهول يلحق بذيل عموده الافتراضي، والمحذوف
   * يسقط — بلا أن يفقد المستخدمُ ترتيبَه كلَّه، وبلا أن يختفي قسمٌ جديد
   * بلا سبب.
   */
  const known = useMemo(() => new Set(defs.map((d) => d.key)), [defs]);
  const resolved = useMemo<Layout>(() => {
    const keep = (keys: string[]) => keys.filter((k) => known.has(k));
    /* ترتيبٌ محفوظٌ قبل وجود العمود الثالث لا يذكره — فيُقرأ فارغًا لا معدومًا. */
    const full = keep(layout.order.full ?? []);
    const main = keep(layout.order.main);
    const side = keep(layout.order.side);
    const placed = new Set([...full, ...main, ...side]);
    const bucket = { full, main, side };
    defs.forEach((d) => {
      if (placed.has(d.key)) return;
      bucket[d.column].push(d.key);
    });
    return {
      order: { full, main, side },
      hidden: layout.hidden.filter((k) => known.has(k)),
    };
  }, [layout, defs, known]);

  const persist = useCallback(
    (next: Layout) => {
      setLayout(next);
      try {
        localStorage.setItem(storageKey, JSON.stringify(next));
      } catch {
        /* حفظُ تفضيلٍ ليس جوهر الشاشة: تعذّرُه لا يُعطّلها. */
      }
    },
    [storageKey],
  );

  const titleOf = useCallback(
    (key: string) => defs.find((d) => d.key === key)?.title ?? key,
    [defs],
  );

  const move = useCallback(
    (dragKey: string, overKey: string) => {
      if (dragKey === overKey) return;
      const next: Layout = {
        order: {
          full: [...resolved.order.full],
          main: [...resolved.order.main],
          side: [...resolved.order.side],
        },
        hidden: [...resolved.hidden],
      };
      const from = COLUMNS.find((c) => next.order[c].includes(dragKey));
      const to = COLUMNS.find((c) => next.order[c].includes(overKey));
      if (!from || !to) return;
      next.order[from] = next.order[from].filter((k) => k !== dragKey);
      const at = next.order[to].indexOf(overKey);
      next.order[to].splice(at, 0, dragKey);
      persist(next);
    },
    [resolved, persist],
  );

  /* الإفلات في فراغ العمود — نقلٌ إلى ذيله، وبه يُنقل قسمٌ بين العمودين. */
  const moveToColumn = useCallback(
    (dragKey: string, column: ColumnKey) => {
      const next: Layout = {
        order: {
          full: [...resolved.order.full],
          main: [...resolved.order.main],
          side: [...resolved.order.side],
        },
        hidden: [...resolved.hidden],
      };
      const from = COLUMNS.find((c) => next.order[c].includes(dragKey));
      if (!from) return;
      if (from === column && next.order[column].at(-1) === dragKey) return;
      next.order[from] = next.order[from].filter((k) => k !== dragKey);
      next.order[column].push(dragKey);
      persist(next);
    },
    [resolved, persist],
  );

  const hide = useCallback(
    (key: string) => {
      if (resolved.hidden.includes(key)) return;
      persist({ ...resolved, hidden: [...resolved.hidden, key] });
    },
    [resolved, persist],
  );

  const restore = useCallback(
    (key: string) => persist({ ...resolved, hidden: resolved.hidden.filter((k) => k !== key) }),
    [resolved, persist],
  );

  const reset = useCallback(() => {
    persist(defaults);
    setZoomed(null);
  }, [persist, defaults]);

  const placement = useCallback(
    (key: string) => {
      for (const c of COLUMNS) {
        const i = resolved.order[c].indexOf(key);
        if (i >= 0) return { column: c, index: i };
      }
      return null;
    },
    [resolved],
  );

  const registerColumn = useCallback((column: ColumnKey, el: HTMLElement | null) => {
    setCols((prev) => (prev[column] === el ? prev : { ...prev, [column]: el }));
  }, []);

  /* Esc يخرج من التكبير ثم من وضع التحرير — الأقرب أولًا. */
  useEffect(() => {
    if (!zoomed && !editing) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (zoomed) setZoomed(null);
      else setEditing(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [zoomed, editing]);

  return {
    order: resolved.order,
    hidden: resolved.hidden,
    editing,
    setEditing,
    zoomed,
    setZoomed,
    dragging,
    setDragging,
    move,
    moveToColumn,
    hide,
    restore,
    reset,
    titleOf,
    placement,
    cols,
    registerColumn,
    zoomEl,
    setZoomEl,
  };
}

export type ProfileLayoutApi = ReturnType<typeof useProfileLayout>;

/** زرّ الرأس — يفتح وضع التحرير ويغلقه. */
export function ProfileLayoutButton({ api }: { api: ProfileLayoutApi }) {
  return (
    <button
      className={`btn btn-sm${api.editing ? ' btn-primary' : ''}`}
      onClick={() => {
        api.setEditing(!api.editing);
        api.setZoomed(null);
      }}
      title="اسحب الأقسام لترتيبها، أو أخفِ ما لا تحتاجه"
      aria-pressed={api.editing}
    >
      <Icon name="layers" size={15} />
      {api.editing ? 'إنهاء التغيير' : 'تغيير النمط'}
    </button>
  );
}

/** شريط وضع التحرير — يشرح ما يجري، ويعيد المخفيّ، ويردّ الترتيب الأصلي. */
export function ProfileLayoutBar({ api }: { api: ProfileLayoutApi }) {
  if (!api.editing) return null;
  return (
    <div className="pl-bar">
      <span className="pl-bar-hint">
        <Icon name="layers" size={14} />
        اسحب أيّ قسمٍ إلى موضعه — في عموده أو إلى المقابل — أو أخفِه بعلامة ×.
      </span>

      {api.hidden.length > 0 && (
        <span className="pl-bar-hidden">
          <span className="tiny mute">المخفيّة:</span>
          {api.hidden.map((k) => (
            <button key={k} className="pl-chip" onClick={() => api.restore(k)}>
              <Icon name="plus" size={12} />
              {api.titleOf(k)}
            </button>
          ))}
        </span>
      )}

      <button className="btn btn-sm" style={{ marginInlineStart: 'auto' }} onClick={api.reset}>
        <Icon name="refresh" size={13} />
        الترتيب الأصلي
      </button>
    </div>
  );
}

/**
 * إطار القسم.
 *
 * يلفّ ما كان يُكتب في الصفحة مباشرةً فيكسبه مقبضًا للسحب وزرّ إخفاءٍ
 * وزرّ تكبير — بلا أن يمسّ محتواه. وخارج وضع التحرير يبقى شفّافًا: لا
 * إطارَ ولا أزرارَ إلا التكبير عند المرور.
 */
export function Section({ layoutKey, children }: { layoutKey: string; children: ReactNode }) {
  const ctx = useContext(Ctx);
  const host = useContext(HostColumn);
  const [over, setOver] = useState(false);

  /* بلا مزوّد: القسم كما كان قبل النمط — الصفحة لا تنكسر بغيابه. */
  if (!ctx) return <>{children}</>;
  if (ctx.hidden.has(layoutKey)) return null;

  const place = ctx.placement(layoutKey);
  const zoomedMe = ctx.zoomed === layoutKey;
  /* التكبير يُفرد المكبَّر: ما سواه ينسحب من الشاشة حتى يُغلق. */
  if (ctx.zoomed && !zoomedMe) return null;

  const title = ctx.titleOf(layoutKey);
  const dragging = ctx.dragging === layoutKey;

  const body = (
    <div
      className={`pl-sec${ctx.editing ? ' is-edit' : ''}${dragging ? ' is-dragging' : ''}${
        over ? ' is-over' : ''
      }${zoomedMe ? ' is-zoomed' : ''}`}
      style={zoomedMe ? undefined : { order: place?.index ?? 0 }}
      draggable={ctx.editing && !ctx.zoomed}
      onDragStart={(e) => {
        if (!ctx.editing) return;
        e.dataTransfer.setData('text/plain', layoutKey);
        e.dataTransfer.effectAllowed = 'move';
        ctx.setDragging(layoutKey);
      }}
      onDragEnd={() => {
        ctx.setDragging(null);
        setOver(false);
      }}
      onDragOver={(e) => {
        if (!ctx.editing || !ctx.dragging || ctx.dragging === layoutKey) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        if (!ctx.editing) return;
        e.preventDefault();
        e.stopPropagation();
        const key = e.dataTransfer.getData('text/plain');
        setOver(false);
        if (key) ctx.move(key, layoutKey);
        ctx.setDragging(null);
      }}
    >
      {/* شريط القسم: اسمه ومقبضه في التحرير، وأزراره عند المرور. */}
      <div className="pl-sec-bar">
        {ctx.editing && (
          <span className="pl-grip" title="اسحب لتغيير الموضع">
            <Icon name="menu" size={13} />
            {title}
          </span>
        )}
        <span className="pl-sec-acts">
          <button
            type="button"
            className="pl-icon"
            title={zoomedMe ? 'إغلاق التكبير (Esc)' : 'تكبير القسم ليملأ الشاشة'}
            aria-label={zoomedMe ? 'إغلاق التكبير' : `تكبير ${title}`}
            onClick={() => ctx.setZoomed(zoomedMe ? null : layoutKey)}
          >
            <Icon name={zoomedMe ? 'x' : 'expand'} size={13} />
          </button>
          {ctx.editing && !ctx.zoomed && (
            <button
              type="button"
              className="pl-icon pl-icon-x"
              title="إخفاء القسم — يعود من شريط المخفيّة"
              aria-label={`إخفاء ${title}`}
              onClick={() => ctx.hide(layoutKey)}
            >
              <Icon name="x" size={13} />
            </button>
          )}
        </span>
      </div>

      {children}
    </div>
  );

  /* المكبَّر يُرسم في طبقته الثابتة، والمنقولُ في عموده الجديد. */
  if (zoomedMe) return ctx.zoomEl ? createPortal(body, ctx.zoomEl) : null;
  if (place && place.column !== host) {
    const target = ctx.columnEl(place.column);
    return target ? createPortal(body, target) : null;
  }
  return body;
}

/**
 * عمودٌ من الأقسام.
 *
 * يستقبل ما كُتب فيه، ويستضيف ما انتقل إليه من العمود المقابل، ويقبل
 * الإفلات في فراغه فينتقل القسم إلى ذيله.
 */
export function LayoutColumn({
  api,
  column,
  children,
}: {
  api: ProfileLayoutApi;
  column: ColumnKey;
  children: ReactNode;
}) {
  /* أثناء التكبير: العمودان يفرغان — المكبَّر وحده في طبقته. */
  const hiddenByZoom = Boolean(api.zoomed);

  /*
   * مرجعٌ ثابت لا يُعاد بناؤه مع كل رسمة.
   *
   * دالةٌ جديدةٌ في كل رسمة تجعل React يفصل العنصر ويعيد وصله — فيُسجَّل
   * `null` ثم العنصر، وكلٌّ منهما يغيّر الحالة فيُعيد الرسم: حلقةٌ لا
   * تنتهي («Maximum update depth»). وقد وقعت فعلًا.
   */
  const setRef = useCallback(
    (el: HTMLDivElement | null) => api.registerColumn(column, el),
    [api.registerColumn, column],
  );

  return (
    <HostColumn.Provider value={column}>
      <div
        ref={setRef}
        className={`pl-col${api.editing ? ' is-edit' : ''}`}
        hidden={hiddenByZoom}
        onDragOver={(e) => {
          if (!api.editing || !api.dragging) return;
          e.preventDefault();
        }}
        onDrop={(e) => {
          if (!api.editing) return;
          const key = e.dataTransfer.getData('text/plain');
          if (key) api.moveToColumn(key, column);
          api.setDragging(null);
        }}
      >
        {children}
        {api.editing && api.order[column].filter((k) => !api.hidden.includes(k)).length === 0 && (
          <p className="tiny mute center pl-empty">أفلت قسمًا هنا.</p>
        )}
      </div>
    </HostColumn.Provider>
  );
}

function ZoomHost({ api }: { api: ProfileLayoutApi }) {
  const setZoom = api.setZoomEl;
  const setRef = useCallback((el: HTMLDivElement | null) => setZoom(el), [setZoom]);
  return <div className="pl-zoom-host" ref={setRef} role="dialog" aria-modal />;
}

/** يوفّر حالة النمط لأقسام الصفحة، ويحمل طبقة التكبير. */
export function ProfileLayoutProvider({
  api,
  children,
}: {
  api: ProfileLayoutApi;
  children: ReactNode;
}) {
  const value = useMemo<LayoutState>(
    () => ({
      editing: api.editing,
      zoomed: api.zoomed,
      hidden: new Set(api.hidden),
      dragging: api.dragging,
      setDragging: api.setDragging,
      move: api.move,
      moveToColumn: api.moveToColumn,
      hide: api.hide,
      setZoomed: api.setZoomed,
      titleOf: api.titleOf,
      placement: api.placement,
      columnEl: (c: ColumnKey) => api.cols[c],
      registerColumn: api.registerColumn,
      zoomEl: api.zoomEl,
    }),
    [api],
  );

  /* التكبير يمنع تمرير الصفحة خلفه — كنافذةٍ تملأ الشاشة. */
  useEffect(() => {
    if (!api.zoomed) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [api.zoomed]);

  return (
    <Ctx.Provider value={value}>
      {children}
      {/* طبقة التكبير — مرجعُها ثابتٌ للسبب نفسه الذي في العمود. */}
      {api.zoomed && <ZoomHost api={api} />}
    </Ctx.Provider>
  );
}
